/**
 * Document Scan Component
 * Component for scanning and uploading documents for certificate applications
 */

import React, { useState, useRef, useEffect } from "react";
import type { CertificateApplication } from "../types/certificate.types";
import BusyOverlay from "../components/common/BusyOverlay";
import { showSuccessToast, showErrorToast, showConfirm } from "../utils/toast";
import {
  GENERIC_ERROR_MESSAGE,
  REQUIRED_FIELD_MESSAGE,
} from "../utils/constants";
import { getFullPath } from "../utils/getFullPath";
import { apiRequest } from "../api/apiClient";
import "../styles/pages/Users.css";

interface DocumentScanProps {
  application: CertificateApplication;
  onClose: () => void;
  onScanSuccess?: () => void;
  title?: string;
  hideDocumentType?: boolean;
  hideDescription?: boolean;
  defaultDocumentTypeId?: number;
  uploadEndpoint?: string;
}

interface DocumentType {
  id: number;
  name: string;
}

export const DocumentScan: React.FC<DocumentScanProps> = ({
  application,
  onClose,
  onScanSuccess,
  title = "Add Document",
  hideDocumentType = false,
  hideDescription = false,
  defaultDocumentTypeId,
  uploadEndpoint = "/Attachments",
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scannedFile, setScannedFile] = useState<File | null>(null);
  const [documentTypeId, setDocumentTypeId] = useState<number>(0);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [description, setDescription] = useState<string>("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [scanMode, setScanMode] = useState<"upload" | "scan">("upload");
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Fetch document types on mount
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const response = await apiRequest("/MasterData", { method: "GET" });
        const data = await response.json();
        const types = (Array.isArray(data) ? data : data?.data || [])
          .filter(
            (item: any) =>
              item.category?.toLowerCase() === "document type" && item.isActive,
          )
          .map((item: any) => ({
            id: item.id,
            name: item.name,
          }));
        setDocumentTypes(types);
        if (defaultDocumentTypeId) {
          setDocumentTypeId(defaultDocumentTypeId);
        } else if (types.length > 0) {
          setDocumentTypeId(types[0].id);
        }
      } catch (err) {
        console.error("Error fetching document types:", err);
      }
    };
    fetchDocumentTypes();
  }, []);

  // Cleanup camera stream and preview URL on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [stream, previewUrl]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: 1920, height: 1080 },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setIsCameraActive(true);
      setError("");
    } catch (err: any) {
      console.error("Error accessing camera:", err);
      setError("Failed to access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File(
                [blob],
                `scanned-document-${Date.now()}.jpg`,
                {
                  type: "image/jpeg",
                },
              );
              setScannedFile(file);
              const url = URL.createObjectURL(blob);
              setPreviewUrl(url);
              stopCamera();
            }
          },
          "image/jpeg",
          0.95,
        );
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }

      // Validate file type (images only)
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        setError("Only images (JPEG, PNG, GIF) are allowed");
        return;
      }

      setScannedFile(file);
      setError("");
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.scannedFile;
        return newErrors;
      });

      // Create preview URL for images
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const errors: Record<string, string> = {};
    const fileToUpload = scannedFile;
    const effectiveDocumentTypeId =
      defaultDocumentTypeId && defaultDocumentTypeId > 0
        ? defaultDocumentTypeId
        : documentTypeId;

    // Validation
    if (!fileToUpload) {
      errors.scannedFile = REQUIRED_FIELD_MESSAGE;
    }

    if (
      !hideDocumentType &&
      (!effectiveDocumentTypeId || effectiveDocumentTypeId === 0)
    ) {
      errors.documentTypeId = REQUIRED_FIELD_MESSAGE;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (!fileToUpload) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Upload Document",
      "Are you sure you want to upload this scanned document?",
      "Yes, upload",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setLoading(true);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("certificateApplicationId", application.id!.toString());
      if (effectiveDocumentTypeId > 0) {
        formData.append("documentTypeId", effectiveDocumentTypeId.toString());
      }
      formData.append("description", description || "");

      // Upload document to backend
      const response = await apiRequest(uploadEndpoint, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            errorData.title ||
            `HTTP ${response.status}: ${response.statusText}`,
        );
      }

      showSuccessToast("Document uploaded successfully");

      // Call success callback if provided
      if (onScanSuccess) {
        onScanSuccess();
      }

      // Close the modal
      onClose();
    } catch (err: any) {
      console.error("Error uploading document:", err);
      setError(err?.message || "Failed to upload document");
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setScannedFile(null);
    setPreviewUrl(null);
    setError("");
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.scannedFile;
      return newErrors;
    });
  };

  return (
    <div className="modal-overlay modal-overlay--side-panel">
      <div className="modal-content modal-content--side-panel">
        <BusyOverlay isVisible={loading} message="Uploading document..." />

        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError("")}
                className="alert-close"
              >
                ✕
              </button>
            </div>
          )}

          {/* Application Info */}
          <div
            style={{
              padding: "1rem",
              background: "#f8f9fa",
              borderRadius: "4px",
              marginBottom: "1.5rem",
            }}
          >
            <div style={{ fontWeight: "600", marginBottom: "0.5rem" }}>
              Application: {application.formNo}
            </div>
            <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
              {application.category}
            </div>
          </div>

          {/* Document Type */}
          {!hideDocumentType && (
            <div
              className={
                validationErrors.documentTypeId ? "form-group--error" : ""
              }
              style={{ marginBottom: "1.5rem" }}
            >
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  color: "#374151",
                }}
              >
                Document Type *
              </label>
              <select
                className={`form-input ${validationErrors.documentTypeId ? "form-input--error" : ""}`}
                value={documentTypeId}
                onChange={(e) => {
                  setDocumentTypeId(Number(e.target.value));
                  if (validationErrors.documentTypeId) {
                    setValidationErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.documentTypeId;
                      return newErrors;
                    });
                  }
                }}
                required
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                }}
              >
                {documentTypes.length === 0 ? (
                  <option value="0">Loading...</option>
                ) : (
                  documentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))
                )}
              </select>
              {validationErrors.documentTypeId && (
                <div className="field-error">
                  {validationErrors.documentTypeId}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {!hideDescription && (
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  color: "#374151",
                }}
              >
                Description (Optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the document"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                }}
              />
            </div>
          )}

          {/* Mode Toggle */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.75rem",
                fontWeight: "500",
                fontSize: "0.875rem",
                color: "#374151",
              }}
            >
              Document Input Method *
            </label>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                marginBottom: "1rem",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setScanMode("upload");
                  stopCamera();
                }}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  border:
                    scanMode === "upload"
                      ? "2px solid #28a745"
                      : "1px solid #dee2e6",
                  borderRadius: "4px",
                  background: scanMode === "upload" ? "#e8f5e9" : "#fff",
                  cursor: "pointer",
                  fontWeight: scanMode === "upload" ? "600" : "400",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s",
                }}
                disabled={loading}
              >
                <span style={{ fontSize: "1.25rem" }}>📁</span>
                <span>Upload File</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setScanMode("scan");
                  handleRemoveFile();
                }}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  border:
                    scanMode === "scan"
                      ? "2px solid #28a745"
                      : "1px solid #dee2e6",
                  borderRadius: "4px",
                  background: scanMode === "scan" ? "#e8f5e9" : "#fff",
                  cursor: "pointer",
                  fontWeight: scanMode === "scan" ? "600" : "400",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s",
                }}
                disabled={loading}
              >
                <span style={{ fontSize: "1.25rem" }}>📷</span>
                <span>Scan with Camera</span>
              </button>
            </div>
          </div>

          {/* Upload Mode */}
          {scanMode === "upload" && (
            <div style={{ marginBottom: "1.5rem" }}>
              {scannedFile ? (
                <div
                  style={{
                    padding: "1rem",
                    background: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px solid #dee2e6",
                  }}
                >
                  {previewUrl && (
                    <div style={{ marginBottom: "1rem", textAlign: "center" }}>
                      <img
                        src={getFullPath(previewUrl) || undefined}
                        alt="Document preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "400px",
                          borderRadius: "4px",
                          border: "1px solid #dee2e6",
                        }}
                      />
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "500" }}>
                        {scannedFile.name}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        {scannedFile.type} •{" "}
                        {(scannedFile.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn--sm btn--danger"
                      onClick={handleRemoveFile}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    border: validationErrors.scannedFile
                      ? "2px dashed rgba(200, 29, 43, 0.65)"
                      : "2px dashed #dee2e6",
                    borderRadius: "4px",
                    padding: "2rem",
                    textAlign: "center",
                    background: "#f8f9fa",
                  }}
                >
                  <input
                    type="file"
                    id="documentUpload"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor="documentUpload"
                    style={{
                      cursor: "pointer",
                      display: "block",
                    }}
                  >
                    <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>
                      📄
                    </div>
                    <div style={{ fontWeight: "500", marginBottom: "0.25rem" }}>
                      Click to upload document
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                      Images only (JPEG, PNG, GIF), up to 10MB
                    </div>
                  </label>
                </div>
              )}
              {validationErrors.scannedFile && (
                <div className="field-error">
                  {validationErrors.scannedFile}
                </div>
              )}
            </div>
          )}

          {/* Scan Mode */}
          {scanMode === "scan" && (
            <div style={{ marginBottom: "1.5rem" }}>
              {scannedFile ? (
                <div
                  style={{
                    padding: "1rem",
                    background: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px solid #dee2e6",
                  }}
                >
                  {previewUrl && (
                    <div style={{ marginBottom: "1rem", textAlign: "center" }}>
                      <img
                        src={getFullPath(previewUrl) || undefined}
                        alt="Captured document"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "400px",
                          borderRadius: "4px",
                          border: "1px solid #dee2e6",
                        }}
                      />
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "500" }}>
                        {scannedFile.name}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        Captured Image •{" "}
                        {(scannedFile.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn--sm btn--danger"
                      onClick={handleRemoveFile}
                    >
                      Retake
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {!isCameraActive ? (
                    <div
                      style={{
                        border: validationErrors.scannedFile
                          ? "2px dashed rgba(200, 29, 43, 0.65)"
                          : "2px dashed #dee2e6",
                        borderRadius: "4px",
                        padding: "2rem",
                        textAlign: "center",
                        background: "#f8f9fa",
                      }}
                    >
                      <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>
                        📷
                      </div>
                      <div
                        style={{ fontWeight: "500", marginBottom: "0.25rem" }}
                      >
                        Use your camera to scan the document
                      </div>
                      <div
                        style={{
                          fontSize: "0.875rem",
                          color: "#6b7280",
                          marginBottom: "1rem",
                        }}
                      >
                        Ensure good lighting and all text is visible
                      </div>
                      <button
                        type="button"
                        onClick={startCamera}
                        className="btn btn--primary"
                        style={{
                          background: "#28a745",
                          border: "none",
                          padding: "0.75rem 1.5rem",
                          fontSize: "0.95rem",
                        }}
                      >
                        Open Camera
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        overflow: "hidden",
                        background: "#000",
                      }}
                    >
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        style={{
                          width: "100%",
                          maxHeight: "500px",
                          display: "block",
                        }}
                      />
                      <div
                        style={{
                          padding: "1rem",
                          background: "#fff",
                          display: "flex",
                          gap: "0.5rem",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          type="button"
                          onClick={captureImage}
                          className="btn btn--primary"
                          style={{
                            background: "#28a745",
                            border: "none",
                            padding: "0.75rem 1.5rem",
                            fontSize: "0.95rem",
                          }}
                        >
                          📸 Capture
                        </button>
                        <button
                          type="button"
                          onClick={stopCamera}
                          className="btn btn--outline"
                          style={{
                            padding: "0.75rem 1.5rem",
                            fontSize: "0.95rem",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <canvas ref={canvasRef} style={{ display: "none" }} />
                </div>
              )}
              {validationErrors.scannedFile && (
                <div className="field-error">
                  {validationErrors.scannedFile}
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn--outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading || !scannedFile}
              style={{
                background: loading || !scannedFile ? "#ccc" : "#28a745",
                cursor: loading || !scannedFile ? "not-allowed" : "pointer",
              }}
            >
              {loading ? (
                <>
                  <span className="spinner spinner--sm"></span>
                  Uploading...
                </>
              ) : (
                "Upload Document"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentScan;
